'use client';

import dynamic from 'next/dynamic';
import { useEffect, useRef } from 'react';
import logExploreTrailCardListActionTaken from '@alltrails/amplitude/events/logExploreTrailCardListActionTaken';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Sidebar from '@alltrails/maps/components/Sidebar/Sidebar';
import AskSidebar from '@/components/AskSidebar/AskSidebar';
import MobileResultsList from '@/components/MobileResultsList/MobileResultsList';
import useIsAskShowing from '@/hooks/useIsAskShowing';
import useIsConditionsSidebarMounted from '@/hooks/useIsConditionsSidebarMounted';
import { sidebarId } from '@/utils/constants';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import { setIsMobileListAtTop, setIsShowingMobileList, setPanelCollapsed } from '@/utils/slices/exploreSlice';

const WaypointSidebar = dynamic(() => import('@alltrails/maps/components/Waypoints/WaypointSidebar/WaypointSidebar'), { ssr: false });
const ExploreTrailConditions = dynamic(() => import('@/components/ExploreTrail/ExploreTrailConditions/ExploreTrailConditions'), { ssr: false });

export default function SidebarSlot({ children }: { children: React.ReactNode }) {
  const isMobile = useIsMobileSizedScreen();
  const dispatch = useAppDispatch();
  const { isShowingMobileList, panelCollapsed, hasSelectedWaypoint, currentTrailId, currentTrailSlug } = useAppSelector(state => ({
    isShowingMobileList: state.explore.isShowingMobileList,
    panelCollapsed: state.explore.panelCollapsed,
    // Both `'user'` and `'community'` claims swap in the same sidebar — the component branches internally.
    hasSelectedWaypoint: state.map.selectedWaypoint !== undefined,
    currentTrailId: state.explore.currentTrail?.id,
    currentTrailSlug: state.explore.currentTrail?.slug
  }));

  const isConditionsSidebarMounted = useIsConditionsSidebarMounted();
  const isAskShowing = useIsAskShowing();

  // When a user has clicked a waypoint pin (their own or a community one), the waypoint sidebar replaces the page's
  // normal sidebar contents. A trail page whose conditions panel is open swaps in the same way, behind the waypoint
  // sidebar in precedence, and so does Ask AllTrails on the pages that support it.
  //
  // Note that swapping unmounts the slot's children, so anything they own is rebuilt on the way back: scroll position
  // is lost, and their mount-time analytics (ExploreTrailSidebar's experiment exposure and section-viewed events)
  // fire again. Worth revisiting if toggling turns out to be frequent.
  let sidebarContent = children;
  if (hasSelectedWaypoint) {
    sidebarContent = <WaypointSidebar entryPoint="explore" preferClientNavigation trailSlug={currentTrailSlug} />;
  } else if (isConditionsSidebarMounted && currentTrailId !== undefined) {
    sidebarContent = <ExploreTrailConditions trailId={currentTrailId} />;
  } else if (isAskShowing) {
    sidebarContent = <AskSidebar />;
  }

  const wasMobile = useRef(isMobile);
  useEffect(() => {
    // It is very confusing if resizing the screen shows the mobile list because as the screen continues to shrink, the list results change for seemingly no reason.
    // Only force the map view on an actual desktop -> mobile transition (a resize); don't clobber a page's
    // initial default on mount (e.g. the members lists page intentionally defaults to the mobile list view).
    if (isMobile && !wasMobile.current) {
      dispatch(setIsShowingMobileList(false));
    }
    wasMobile.current = isMobile;
  }, [dispatch, isMobile]);

  useEffect(() => {
    if (!isShowingMobileList) {
      // Reset when the list is hidden so that when the list is shown again, we recognize that its at the top once again.
      dispatch(setIsMobileListAtTop(true));
    }
  }, [dispatch, isShowingMobileList]);

  return isMobile ? (
    <>{isShowingMobileList && <MobileResultsList>{sidebarContent}</MobileResultsList>}</>
  ) : (
    <Sidebar
      collapsed={panelCollapsed}
      id={sidebarId}
      onCollapseRequest={() => {
        if (panelCollapsed) {
          logExploreTrailCardListActionTaken({ action_taken: 'window toggled open' });
          dispatch(setPanelCollapsed(false));
        } else {
          logExploreTrailCardListActionTaken({ action_taken: 'window toggled closed' });
          dispatch(setPanelCollapsed(true));
        }
      }}
    >
      {sidebarContent}
    </Sidebar>
  );
}
